import { useMemo } from 'react'
import { numberFormat, reduce } from 'shared/util'
import { Virtuoso } from 'react-virtuoso'
import { useSearch, useTargetList } from 'features/search/context'
import { KojinResult } from 'interface/kojin'

import {
  Box,
  Alert,
  Stack,
  Button,
  Typography,
  StackProps,
} from '@mui/material'
import { DownloadIcon } from 'shared/icons'

import { useKojinList } from './container'
import { KojinRecord } from './components/record'
import { KojinListBar } from './components/bar'

import { KojinListDownloadDialog } from './dialog/downloadDialog'

import { useKojinListDownload } from './container/useDownload'

import { AuthorGraphDialog } from 'features/kojin/dialog/authorGraph'
import { KojinPreviewDialog } from 'features/kojin/dialog/preview'

import { usePrediction } from 'features/prediction/hooks'
import { PredictionBtn } from 'features/prediction/components/predictionBtn'
import { KojinViewMode } from 'features/kojin/analytics/types'
import { SearchStatus } from 'interface/search'
import { useUserSession } from 'features/session/context'

type KojinListProps = {
  kojinList: KojinResult[]
  predictionState: ReturnType<typeof usePrediction>
  openPredictionDialog: (kojinList: KojinResult[]) => void
  viewMode: KojinViewMode
  onClickViewMode: (viewMode: KojinViewMode) => void
} & StackProps

export const KojinList = ({
  kojinList,
  predictionState,
  openPredictionDialog,
  viewMode,
  onClickViewMode,
  ...others
}: KojinListProps) => {
  const search = useSearch()
  const targetList = useTargetList()
  const { isCareNet } = useUserSession()

  const searchFlags = useMemo(
    () => {
      const { hasDpc, hasCancer } = reduce(search?.disease, (status, disease) => ({
        hasDpc: status.hasDpc || (disease.dpcCode !== null),
        hasCancer: status.hasCancer || (disease.cancerCode !== null)
      }), {
        hasDpc: false,
        hasCancer: false
      })
      const hasJournal = (search?.journalCount ?? 0) > 0 ? true : false
      const hasTargetList = targetList ? true : false
      const hasExtend = (targetList?.extendCount ?? 0) > 0 ? true : false
      return {
        hasDpc, hasCancer, hasJournal,
        hasTargetList, hasExtend
      }
    },
    [search, targetList]
  )
  const {
    filteredList,
    searchWord,
    onChangeSearchWord,
    orderBy,
    onChangeOrderBy,
    showUnMatch,
    onChangeShowUnMatch,
    modal,
    openAuthorGraphDialog,
    closeModal,
    selectedKojin,
    onSelectKojin,
    onSelectAuthor
  } = useKojinList({
    searchFlags,
    kojinList
  })
  const {
		openDownloadDialog,
		closeDownloadDialog,
    showDownloadDialog,
		downloading,
    downloadProgress,
		downloadSummary,
		doDownload,
  } = useKojinListDownload({
    list: filteredList
  })
  return (
    <Stack direction="column" overflow="hidden" height="100%" {...others}>
      <KojinListBar
        searchFlags={searchFlags}
        searchWord={searchWord}
        onChangeSearchWord={onChangeSearchWord}
        orderBy={orderBy}
        onChangeOrderBy={onChangeOrderBy}
        showUnMatch={showUnMatch}
        onChangeShowUnMatch={onChangeShowUnMatch}
        viewMode={viewMode}
        onClickViewMode={onClickViewMode}
        sx={{
          borderBottom: 1,
          borderColor: 'divider'
        }}
      />
      <Stack flexGrow={1} direction="column" overflow="hidden" position="relative">
        <Box flexGrow={1}>
          {!search || !filteredList ? (
            <></>
          ) : filteredList.length === 0 ? (
            <Box padding={1}>
              <Alert severity="info">対象が見つかりません.</Alert>
            </Box>
          ) : (
            <Virtuoso
              style={{
                height: '100%',
              }}
              data={filteredList}
              itemContent={(index, kojin) => (
                <KojinRecord
                  key={`kojin-${index}`}
                  search={search}
                  kojin={kojin}
                  rank={index + 1}
                  selected={kojin.kojinCode === selectedKojin?.kojinCode}
                  onSelectedKojin={onSelectKojin}
                  onSelectedAuthor={onSelectAuthor}
                  viewMode={viewMode}
                />
              )}
            />
          )}
        </Box>

        <Stack
          direction="row"
          padding={1}
          spacing={1}
          sx={{ borderTop: 1, borderColor: 'divider' }}
          justifyContent="flex-start"
          alignItems="center"
        >
          <Typography variant="button" component="div" flexGrow={1} paddingX={1}>
            {numberFormat(filteredList?.length, '-')} 人
          </Typography>

          {isCareNet && (
            <PredictionBtn
              state={predictionState}
              kojinList={filteredList}
              onClick={(e) => {
                e.preventDefault()
                openPredictionDialog(filteredList)
              }}
            />
          )}

          {search?.status === SearchStatus.Finished && (
            <Button
              variant="outlined"
              sx={{ whiteSpace: 'nowrap' }}
              disabled={!filteredList || filteredList.length === 0}
              onClick={(e) => {
                e.preventDefault()
                openAuthorGraphDialog()
              }}
            >
              相関図
            </Button>
          )}

          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            sx={{ whiteSpace: 'nowrap' }}
            disabled={!filteredList || filteredList.length === 0}
            onClick={(e) => {
              e.preventDefault()
              openDownloadDialog()
            }}
          >
            ダウンロード
          </Button>
        </Stack>

        {modal === 'authorGraph' ? (
          <AuthorGraphDialog
            open
            kojinList={filteredList}
            onClose={closeModal}
          />
        ) : (showDownloadDialog && downloadSummary) ? (
          <KojinListDownloadDialog
            downloading={downloading}
            downloadProgress={downloadProgress}
            targetCount={downloadSummary.targetCount}
            extendCount={downloadSummary.extendCount}
            defaultTitle={targetList?.listName ?? search?.title ?? 'kojinList'}
            onDownload={doDownload}
            onClose={closeDownloadDialog}
          />
        ) : <></>}

        {(filteredList && selectedKojin) && (
          <KojinPreviewDialog
            kojinList={filteredList}
            kojin={selectedKojin ?? undefined}
            onClose={() => onSelectKojin(null)}
          />
        )}
      </Stack>
    </Stack>
  )
}
